import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Spacer,
  TabPanel,
  TabPanels,
  Tabs,
  Tooltip,
} from '@chakra-ui/react';
import { ChevronDownIcon, CloseIcon, RepeatIcon, SearchIcon } from '@chakra-ui/icons';
import {
  useBoolean,
  usePaginationSessionStorage,
  useTabStorage,
} from '@laxmimanogna/code-quick-components';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import CQContainer from '../../components/layout/CQContainer';
import AppColors from '../../constants/AppColors';
import { FONT_FAMILY } from '../../constants/Theme';
import { withProvider } from '../../hoc/withProvider';
import AccountsProvider, {
  AccountContext,
} from '../../providers/AccountsProvider';
import AddHealthSystemAccountForm from './forms/AddHealthSystemAccountForm';
import AccountsTable from './table/AccountsTable';
import NewAccountsTableV3 from './table/NewAccountsTableV3';
import { ACCOUNTS_PAGE_NAME, ACCOUNTS_TABLE_NAME } from './constants';
import { ACCOUNTS_FILTER_OPTIONS, ACCOUNTS_FILTER_OPTIONS_TO_ACCOUNT_QUERY_MAP } from '../../constants/constants';


const AllAccountsTab = [
  
  {
    title: 'All Charts',
    header: 'All Charts',
    component: NewAccountsTableV3,
  },
];

const ARCHIVED_FILTER_NAME = 'Archived';

function AccountsContainerV2() {

  const tabStorage = useTabStorage();
  const {
    getPaginationSession,
    setPaginationSession
  } = usePaginationSessionStorage();
  const accountsContext = useContext(AccountContext);
  const [selectedTab, setSelectedTab] = useState(0);
  const [resetFilterTrigger, rftState] = useBoolean(null);
  const DEFAULT_FILTER_NAME = useMemo(() => "Active", []);
  const DEFAULT_FILTER_POS = useMemo(() => ACCOUNTS_FILTER_OPTIONS.indexOf(DEFAULT_FILTER_NAME), [DEFAULT_FILTER_NAME])
  const { 
    accountFilters, 
    handleApplyFilter,
    isLoadingAccountsList
   } = accountsContext;

  const [accountFormVisible, afvState] = useBoolean(false);

  const dropdownOptions = useMemo(
    () => ACCOUNTS_FILTER_OPTIONS.map(val => ({ value: val, label: val })),
    [ACCOUNTS_FILTER_OPTIONS]
  );

  const [selectedFilter, setSelectedFilter] = useState(() => ({
    value: ACCOUNTS_FILTER_OPTIONS[tabStorage.getTab() ?? DEFAULT_FILTER_POS],
    label: ACCOUNTS_FILTER_OPTIONS[tabStorage.getTab() ?? DEFAULT_FILTER_POS]
  }));

  function handleOpenAddAccount() {
    afvState.on();
  }

  const onSearchAction = () => {
    handleApplyFilter({
      ...accountFilters,
      triggerSearch: !accountFilters.triggerSearch,
    });
  };


  const onFilterChange = e => {
    handleApplyFilter({
      ...accountFilters,
      search: e.target.value
    })
  };

  const onClearSearch = () => {
    handleApplyFilter({
      ...accountFilters,
      search: '',
      triggerSearch: !accountFilters.triggerSearch,
    });
  };

  const isArchivedView = selectedFilter.value === ARCHIVED_FILTER_NAME;

  const onToggleArchived = showArchived => {
    const option = dropdownOptions.find(
      o => o.value === (showArchived ? ARCHIVED_FILTER_NAME : DEFAULT_FILTER_NAME)
    );
    setSelectedFilter(option);
    handleApplyFilter({
      ...accountFilters,
      triggerSearch: !accountFilters.triggerSearch,
      accountType: option.value
    });
  };

  const hasActiveFilters =
    isArchivedView ||
    !!accountFilters?.search ||
    !!accountFilters?.ordering;

  const onResetFilters = () => {
    setSelectedFilter(dropdownOptions[DEFAULT_FILTER_POS]);
    setPaginationSession(
      ACCOUNTS_PAGE_NAME,
      ACCOUNTS_TABLE_NAME,
      {
        ...getPaginationSession(ACCOUNTS_PAGE_NAME, ACCOUNTS_TABLE_NAME),
        page: 1,
        size: 10
      }
    );
    handleApplyFilter({
      search: "",
      triggerSearch: !accountFilters.triggerSearch,
      ordering: "",
      accountType: DEFAULT_FILTER_NAME,
    });
    rftState.toggle();
  };

   useEffect(() => {
      if (!selectedFilter.value) {
        return;
      }

      tabStorage.setTab(ACCOUNTS_FILTER_OPTIONS.indexOf(selectedFilter.value))
    }, [selectedFilter]);


  return (
          <CQContainer>
            <HStack mb={5}>
              <Heading as="h5" fontSize="24px">
                Accounts
              </Heading>
              <Spacer />
              <Button
              variant="solid"
              style={{height:'50px'}}
              borderRadius={25}
              fontWeight={500}
              backgroundColor={AppColors.primary}
              width={'186px'}
              paddingInline={'80px'}
              onClick={handleOpenAddAccount}
              height={'40px'}
              color={'white'}
              outline={`2px`}
              _hover={{
                transition: 'all .1s ease',
                bgColor: AppColors.white,
                color: AppColors.primary,
                outline: `2px solid ${AppColors.primary}`,
              }}
            >
              + Add New Account
            </Button>
            </HStack>
            <Box bg="white" p={4} w="100%" style={{ borderRadius: '12px' }} marginLeft={0}>
              <Tabs
                fontFamily={FONT_FAMILY.baiJamurjee}
                onChange={setSelectedTab}
                index={selectedTab}
              >
                <Flex
                  align={{ base: 'stretch', md: 'center' }}
                  direction={{ base: 'column', md: 'row' }}
                  gap={3}
                  pb={4}
                  mb={2}
                  borderBottom="1px solid"
                  borderColor="gray.200"
                >
                  {/* View: reads as a title; archived accounts are only reachable via the arrow menu */}
                  <Menu placement="bottom-start" isLazy>
                    <MenuButton
                      as={Button}
                      variant="ghost"
                      px={2}
                      ml={-2}
                      alignSelf="flex-start"
                      fontSize="lg"
                      fontWeight={600}
                      color={isArchivedView ? 'gray.600' : 'gray.800'}
                      rightIcon={<ChevronDownIcon color="gray.500" />}
                      _hover={{ bg: 'gray.50' }}
                      _active={{ bg: 'gray.100' }}
                      aria-label="Switch between active and archived"
                      isDisabled={isLoadingAccountsList}
                    >
                      {isArchivedView ? 'Archived' : 'Active'}
                    </MenuButton>
                    <MenuList zIndex={10} minW="160px" py={1}>
                      <MenuOptionGroup
                        type="radio"
                        value={isArchivedView ? ARCHIVED_FILTER_NAME : DEFAULT_FILTER_NAME}
                        onChange={value => {
                          const showArchived = value === ARCHIVED_FILTER_NAME;
                          if (showArchived !== isArchivedView) {
                            onToggleArchived(showArchived);
                          }
                        }}
                      >
                        <MenuItemOption value={DEFAULT_FILTER_NAME} fontSize="sm">
                          Active
                        </MenuItemOption>
                        <MenuItemOption value={ARCHIVED_FILTER_NAME} fontSize="sm">
                          Archived
                        </MenuItemOption>
                      </MenuOptionGroup>
                    </MenuList>
                  </Menu>

                  <Spacer display={{ base: 'none', md: 'block' }} />

                  <HStack spacing={2}>
                    {/* Search */}
                    <InputGroup size="md" w={{ base: '100%', md: '320px' }}>
                      <InputLeftElement pointerEvents="none">
                        <SearchIcon color="gray.400" />
                      </InputLeftElement>
                      <Input
                        value={accountFilters?.search ?? ''}
                        onChange={onFilterChange}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            onSearchAction();
                          }
                        }}
                        placeholder={`Search ${isArchivedView ? 'archived ' : ''}accounts and press Enter`}
                        borderRadius="md"
                        borderColor="gray.300"
                        focusBorderColor={AppColors.secondary}
                        isDisabled={isLoadingAccountsList}
                      />
                      {accountFilters?.search && (
                        <InputRightElement>
                          <IconButton
                            size="xs"
                            variant="ghost"
                            aria-label="Clear search"
                            icon={<CloseIcon boxSize={2.5} />}
                            onClick={onClearSearch}
                            isDisabled={isLoadingAccountsList}
                          />
                        </InputRightElement>
                      )}
                    </InputGroup>

                    {/* Reset */}
                    <Tooltip label="Reset search, sorting and view" hasArrow openDelay={300}>
                      <Button
                        size="md"
                        variant="outline"
                        leftIcon={<RepeatIcon />}
                        fontWeight={500}
                        color="gray.700"
                        borderColor="gray.300"
                        flexShrink={0}
                        isDisabled={isLoadingAccountsList || !hasActiveFilters}
                        onClick={onResetFilters}
                      >
                        Reset
                      </Button>
                    </Tooltip>
                  </HStack>
                </Flex>

      
                <TabPanels>
                  {AllAccountsTab.map((aa, index) => {
                    return (
                      <TabPanel p={0} key={`assigned-audit-panel-${index}`}>
                        <aa.component 
                        key={`my-charts-panel-${index}`} 
                        PAGE_NAME={ACCOUNTS_PAGE_NAME}
                        TABLE_NAME={ACCOUNTS_TABLE_NAME}
                        showArchived={isArchivedView}
                        filterResetTrigger={resetFilterTrigger}
                        />
                      </TabPanel>
                    );
                  })}
                </TabPanels>
              </Tabs>
            </Box>

            {/* FORM MODAL */}
            <AddHealthSystemAccountForm
              isOpen={accountFormVisible}
              onClose={afvState.off}
            />
          </CQContainer>
        );
}

export default withProvider(AccountsProvider, AccountsContainerV2);
